import {
  SESSIONS_TEST_IDS,
  type SessionLayer,
  TURN_LAYERS,
} from '@/components/admin/sessions/contracts';
import { stepAt } from '@/lib/conversation-model';
import {
  type GlobalRule,
  type Guardrail,
  type SessionDetail,
  sessionFigures,
  stepBandAt,
} from '@matter_extractor/shared';
import { ChevronRight } from 'lucide-react';
import {
  Fragment,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BubbleRow, bubbleFoldKey } from './bubble-row';
import { bubbleVisitOfHash, bubblesOf, conversationFil } from './conversation-fil';
import { ConversationHead } from './conversation-head';
import { LayerBar } from './layer-bar';
import { PageBandRow } from './page-band';
import { PlayedProtocolDrawer } from './played-protocol-drawer';
import { conversationSources } from './sources';
import { StateColumn } from './state-column';
import { StepBandRow } from './step-band';
import { type LinkCopy, TurnRow } from './turn-row';

/**
 * The conversation of the protocol (§12.3), assembled.
 *
 * CONTROLLED, like every surface of this lot: the route owns what travels in
 * the address bar - the switches of the bar and the turn a link arrives on -
 * and this holds what does not. The split is §12.3's own: « les bascules voyagent dans l'URL,
 * les dépliages à la main non », because one is a way of reading a whole session
 * and the other is a hand opening one drawer.
 *
 * ## Three states and only one of them is a boolean per layer
 *
 * `revealed` holds the turns whose « + » was pressed: their five accordions
 * exist, closed. `folds` holds the folds a HAND changed, as an OVERRIDE of the
 * switch rather than as a second « open » set - otherwise closing one accordion
 * under a switch that is on would set a flag nothing reads, and the fold would
 * refuse to close. A switch pressed wipes the overrides of its own layer, which
 * is what makes it authoritative: « une bascule ouvre ou ferme cette couche sur
 * TOUTE la conversation ».
 *
 * ## What « Tout replier » does, exactly
 *
 * It closes the five accordions, forgets every « + » pressed and every fold
 * opened by hand, and leaves État where it was. That is the mockup's own
 * behaviour, and the reason is in the gesture's name: it makes the fil BARE,
 * which is its arrival state - and the arrival state has État open.
 *
 * ## The État column stays on the last turn looked at (issue #172)
 *
 * Hover and FOCUS both move it - a reader walking the turns with the arrows
 * must see the state follow - and NOTHING moves it back: leaving the fil leaves
 * the column where it was, which is what lets the pointer travel to it without
 * the state switching under it. There is no pin any more, and nothing a hover
 * does reaches the URL. Before any turn is looked at it shows the END state -
 * « une colonne vide à côté d'un transcript n'apprendrait rien » - and no turn
 * carries the bar.
 *
 * ## The pages played in the conversation stand in the fil (issue #186)
 *
 * Between the last turn and the farewell (prolongation.md §7), each under its band
 * and as a bubble row that is NOT a turn: no number, no arrow stop, no État column
 * to move. What the screen holds for them is the turn's own state under other
 * keys - the bubbles whose « + » was pressed (`revealedBubbles`, by visit), the
 * folds under `b<visite>:<couche>` - and one arrival of its own: the address
 * `#bulle-<visite>`, which the card of a page in the parcours links to, scrolls to
 * the bubble and lights it, as `?tour=` does a turn.
 *
 * ## `?tour=` is an ARRIVAL, and so is a réussi pressed in the column
 *
 * A link that names a turn scrolls to it, lights it for two seconds and starts
 * the column on it, as if it had just been hovered; from there the eye takes
 * over. A réussi objective pressed in the column does the same with the turn it
 * became réussi on (`lastPassage`). Both are one gesture, `goTo`, written once.
 */

export interface ConversationScreenProps {
  detail: SessionDetail;
  guardrails: readonly Guardrail[];
  rules: readonly GlobalRule[];
  budgetPerStepEur: number | null;
  /** The switches of the bar, read from `?couches=`. */
  openLayers: ReadonlySet<SessionLayer>;
  onLayers: (next: ReadonlySet<SessionLayer>) => void;
  /** `?tour=` - the 1-based number of the turn a link arrives on, or null. */
  arrivalTurn: number | null;
  breadcrumb: ReactNode;
  actions: ReactNode;
}

/** How long an arrival lights its turn, and how long « Lien copié » stays (§12.3). */
const FLASH_MS = 2_100;
const COPY_MS = 2_000;
/** How long a hover stays muted after an arrival's scroll, at most, and the beat after it ends. */
const SCROLL_CEILING_MS = 1_500;
const SCROLL_SETTLE_MS = 100;

export function ConversationScreen({
  detail,
  guardrails,
  rules,
  budgetPerStepEur,
  openLayers,
  onLayers,
  arrivalTurn,
  breadcrumb,
  actions,
}: ConversationScreenProps) {
  const [revealed, setRevealed] = useState<ReadonlySet<number>>(() => new Set());
  const [revealedBubbles, setRevealedBubbles] = useState<ReadonlySet<number>>(() => new Set());
  /*
    The bubble an arrival from the parcours lights (by visit), with the counter
    that replays it. `#bulle-<visite>` is an arrival of the bubble as `?tour=` is
    one of a turn - read ONCE, on opening: the router writes the hash before the
    screen mounts, and a refetch of the detail must not light the bubble again.
    The scroll and the two seconds of light are the effect below.
  */
  const [bubbleFlash, setBubbleFlash] = useState<{ visit: number; key: number } | null>(() => {
    const visit = bubbleVisitOfHash(window.location.hash);
    return visit !== null && bubblesOf(detail).some((bubble) => bubble.visitIndex === visit)
      ? { visit, key: 1 }
      : null;
  });
  const [folds, setFolds] = useState<ReadonlyMap<string, boolean>>(() => new Map());
  // The last turn looked at, or null before any: the column then shows the end.
  const [looked, setLooked] = useState<number | null>(arrivalTurn);
  const [flash, setFlash] = useState<{ turn: number; key: number } | null>(null);
  const [copy, setCopy] = useState<{ turn: number; state: LinkCopy } | null>(null);
  const [drawer, setDrawer] = useState(false);
  const [stateFolded, setStateFolded] = useState(true);
  const list = useRef<HTMLOListElement>(null);
  const copyTimer = useRef<number | undefined>(undefined);
  // True while an arrival scrolls the page: see the scroll effect below.
  const hoverMuted = useRef(false);

  /** Scroll to a turn, light it, and move the column onto it. */
  const goTo = useCallback((number: number): void => {
    setLooked(number);
    setFlash((was) => ({ turn: number, key: (was?.key ?? 0) + 1 }));
  }, []);

  // `?tour=` is an arrival: on opening, and on every new link followed here.
  useEffect(() => {
    if (arrivalTurn !== null) goTo(arrivalTurn);
  }, [arrivalTurn, goTo]);

  /*
    The scroll runs AFTER the render that lit the turn, so the row it looks for
    is the one on screen. `scrollIntoView` is optional-called: jsdom has none,
    and a test mounting an arrival would otherwise throw.
  */
  useEffect(() => {
    if (flash === null) return;
    const row = list.current?.querySelector<HTMLElement>(`li[data-turn="${flash.turn}"]`);
    /*
      While the page scrolls, the turns slide under a pointer that did not move,
      and Chromium sends them a `mouseenter` each: the column would leave the
      turn it just arrived on for whichever turn ended under the pointer - the
      first capture of this lot showed exactly that. So a HOVER is ignored until
      the scroll ends (`scrollend`, or a ceiling where it is not sent, or when
      nothing had to scroll), a beat more for the last synthetic event. Focus is
      never muted: it is a deliberate gesture.
    */
    hoverMuted.current = true;
    let settle: number | undefined;
    const release = (): void => {
      window.clearTimeout(settle);
      settle = window.setTimeout(() => {
        hoverMuted.current = false;
      }, SCROLL_SETTLE_MS);
    };
    window.addEventListener('scrollend', release, { once: true });
    const ceiling = window.setTimeout(release, SCROLL_CEILING_MS);
    row?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    const timer = window.setTimeout(
      () => setFlash((was) => (was?.key === flash.key ? null : was)),
      FLASH_MS,
    );
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(ceiling);
      window.clearTimeout(settle);
      window.removeEventListener('scrollend', release);
      hoverMuted.current = false;
    };
  }, [flash]);

  useEffect(() => {
    if (bubbleFlash === null) return;
    list.current
      ?.querySelector<HTMLElement>(
        `li[data-testid="${SESSIONS_TEST_IDS.bubble}"][data-visit="${bubbleFlash.visit}"]`,
      )
      ?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    const timer = window.setTimeout(
      () => setBubbleFlash((was) => (was?.key === bubbleFlash.key ? null : was)),
      FLASH_MS,
    );
    return () => window.clearTimeout(timer);
  }, [bubbleFlash]);

  useEffect(() => () => window.clearTimeout(copyTimer.current), []);

  /**
   * « Tour n » puts the absolute link of its turn in the clipboard, the layers
   * open included - they are in the address already, so the link is the
   * current address with `tour` set. The Clipboard API is absent from jsdom
   * and refused on an insecure origin, and both say « Copie refusée » rather
   * than a « Lien copié » over an empty clipboard.
   */
  const copyLink = (number: number): void => {
    const url = new URL(window.location.href);
    url.searchParams.set('tour', String(number));
    const said = (state: LinkCopy): void => {
      setCopy({ turn: number, state });
      window.clearTimeout(copyTimer.current);
      copyTimer.current = window.setTimeout(() => setCopy(null), COPY_MS);
    };
    if (typeof navigator.clipboard?.writeText !== 'function') {
      said('failed');
      return;
    }
    navigator.clipboard.writeText(url.toString()).then(
      () => said('copied'),
      () => said('failed'),
    );
  };

  // Rebuilt only when the reading changes, and never on a hover: the maps below
  // are walked by every layer of every turn, and a hover is what moves the État
  // column forty times a minute.
  const sources = useMemo(
    () => conversationSources({ detail, guardrails, rules, budgetPerStepEur }),
    [detail, guardrails, rules, budgetPerStepEur],
  );
  const figures = useMemo(() => sessionFigures(detail), [detail]);
  // The turns and the pages played in the conversation, in the order the fil draws them.
  const fil = useMemo(() => conversationFil(detail), [detail]);

  const shownNumber = looked ?? detail.turns.length;
  const shown = detail.turns[shownNumber - 1];
  const stateOn = openLayers.has('etat');

  const forgetFolds = (of: (layer: string) => boolean): void =>
    setFolds((was) => new Map([...was].filter(([key]) => !of(key.slice(key.indexOf(':') + 1)))));

  const toggleLayer = (layer: SessionLayer): void => {
    const next = new Set(openLayers);
    if (next.has(layer)) next.delete(layer);
    else next.add(layer);
    if (layer !== 'etat') forgetFolds((name) => name === layer);
    onLayers(next);
  };

  const openAll = (): void => {
    forgetFolds(() => true);
    onLayers(new Set([...openLayers, ...TURN_LAYERS]));
  };

  const closeAll = (): void => {
    setRevealed(new Set());
    setRevealedBubbles(new Set());
    setFolds(new Map());
    onLayers(new Set([...openLayers].filter((layer) => layer === 'etat')));
  };

  const reveal = (number: number): void => {
    setRevealed((was) => {
      const next = new Set(was);
      if (next.has(number)) next.delete(number);
      else next.add(number);
      return next;
    });
    // Folding a turn back also forgets what it had open, which is what the
    // second press promises: « un second clic les referme ».
    setFolds((was) => new Map([...was].filter(([key]) => !key.startsWith(`${number}:`))));
  };

  const revealBubble = (visit: number): void => {
    setRevealedBubbles((was) => {
      const next = new Set(was);
      if (next.has(visit)) next.delete(visit);
      else next.add(visit);
      return next;
    });
    setFolds((was) => new Map([...was].filter(([key]) => !key.startsWith(`b${visit}:`))));
  };

  /**
   * The arrows walk the turns. The guard is what keeps them from stealing the
   * keys of the two buttons a turn carries: only a keystroke on the turn ITSELF
   * moves, so a reader who tabbed into « Tour 4 » still gets the browser's own
   * behaviour there.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLOListElement>): void => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    const target = event.target as HTMLElement;
    if (target.dataset.turn === undefined) return;
    const rows = [...event.currentTarget.querySelectorAll<HTMLElement>('li[data-turn]')];
    const at = rows.indexOf(target);
    if (at < 0) return;
    const to =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? rows.length - 1
          : event.key === 'ArrowDown'
            ? at + 1
            : at - 1;
    const next = rows[to];
    if (next === undefined) return;
    event.preventDefault();
    next.focus();
  };

  return (
    <section data-testid={SESSIONS_TEST_IDS.conversation} className="flex flex-col gap-4">
      <ConversationHead
        detail={detail}
        figures={figures}
        budgetPerStepEur={budgetPerStepEur}
        breadcrumb={breadcrumb}
        actions={actions}
        onOpenPlayedProtocol={() => setDrawer(true)}
      />

      <LayerBar
        open={openLayers}
        onToggle={toggleLayer}
        onOpenAll={openAll}
        onCloseAll={closeAll}
      />

      <div
        className={`grid items-start gap-6 ${stateOn ? 'md:grid-cols-[minmax(0,1fr)_21rem]' : ''}`}
      >
        {stateOn ? (
          <aside
            aria-label="État du protocole"
            /*
              `z-20`, the layer bar's own: a sticky box is a stacking context, so
              the objective panel's `z-30` only counts INSIDE this column, and the
              turns - positioned since #172, for the light of an arrival - would
              paint over a panel that opens left, across the transcript.
            */
            className="relative z-20 flex flex-col gap-2 md:sticky md:top-[calc(var(--spacing-admin-bar)+3.5rem)] md:col-start-2 md:row-start-1"
          >
            {/*
              Under 768 px the column is « un accordéon de plus » (§12.3): the
              same card, folded behind a disclosure. ONE card in the DOM and not
              two - a second copy would be a second `sessions-etat` for a test to
              find and a second subtree to keep in step.
            */}
            <button
              type="button"
              aria-expanded={!stateFolded}
              onClick={() => setStateFolded((was) => !was)}
              className="flex cursor-pointer items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-left font-semibold text-gray-600 text-xs uppercase tracking-wide focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:hidden"
            >
              <ChevronRight
                aria-hidden="true"
                className={`size-3.5 ${stateFolded ? '' : 'rotate-90'}`}
              />
              État du protocole
            </button>
            <div className={stateFolded ? 'hidden md:block' : ''}>
              {/*
                Keyed on the turn shown, so an objective's panel and its timers
                never outlive the snapshot they were reading.
              */}
              <StateColumn
                key={looked ?? 'fin'}
                turn={shown}
                number={shownNumber}
                atEnd={looked === null}
                sources={sources}
                onGoTo={goTo}
              />
            </div>
            <p className="hidden text-gray-500 text-xs md:block">
              Avant tout survol, l’état est celui de la fin. Il suit le tour survolé et y reste. Un
              objectif réussi mène au tour où il l’est devenu.
            </p>
          </aside>
        ) : null}

        <div className="flex min-w-0 flex-col overflow-hidden rounded-md border border-gray-200 bg-white md:col-start-1 md:row-start-1">
          <header className="flex flex-wrap items-center justify-between gap-3 border-gray-100 border-b bg-gray-50 px-5 py-2.5">
            <h2 className="font-semibold text-gray-600 text-xs uppercase tracking-wide">
              Conversation
            </h2>
            <p className="text-gray-500 text-xs">
              Le « + » au survol d’un tour révèle ses cinq accordéons · « Tour N » copie le lien du
              tour · les flèches haut et bas passent d’un tour à l’autre
            </p>
          </header>

          {fil.length === 0 ? (
            <p className="px-5 py-8 text-gray-600 text-sm">
              Cette session n’a joué aucun tour sur la page protocole.
            </p>
          ) : (
            <ol ref={list} onKeyDown={onKeyDown}>
              {fil.map((entry) => {
                if (entry.kind === 'bubble') {
                  const { bubble } = entry;
                  return (
                    <Fragment key={`bulle-${bubble.id}`}>
                      <PageBandRow bubble={bubble} />
                      <BubbleRow
                        bubble={bubble}
                        detail={detail}
                        openLayers={openLayers}
                        revealed={revealedBubbles.has(bubble.visitIndex)}
                        folds={folds}
                        flash={bubbleFlash?.visit === bubble.visitIndex ? bubbleFlash.key : null}
                        onReveal={() => revealBubble(bubble.visitIndex)}
                        onFold={(layer, open) =>
                          setFolds((was) =>
                            new Map(was).set(bubbleFoldKey(bubble.visitIndex, layer), open),
                          )
                        }
                      />
                    </Fragment>
                  );
                }
                const { turn, position } = entry;
                const band = stepBandAt(detail, position);
                const number = position + 1;
                return (
                  <Fragment key={turn.id}>
                    {band === null ? null : (
                      <StepBandRow
                        band={band}
                        farewell={
                          stepAt(detail.cascade.protocol, band.stepId)?.step.farewell ?? false
                        }
                      />
                    )}
                    <TurnRow
                      turn={turn}
                      previous={detail.turns[position - 1]}
                      number={number}
                      sources={sources}
                      openLayers={openLayers}
                      revealed={revealed.has(number)}
                      folds={folds}
                      marked={looked === number}
                      flash={flash?.turn === number ? flash.key : null}
                      copy={copy?.turn === number ? copy.state : null}
                      onReveal={() => reveal(number)}
                      onFold={(layer, open) =>
                        setFolds((was) => new Map(was).set(`${number}:${layer}`, open))
                      }
                      onCopyLink={() => copyLink(number)}
                      onHover={() => {
                        if (!hoverMuted.current) setLooked(number);
                      }}
                      onEnter={() => setLooked(number)}
                    />
                  </Fragment>
                );
              })}
            </ol>
          )}
        </div>
      </div>

      {drawer ? (
        <PlayedProtocolDrawer cascade={detail.cascade} onClose={() => setDrawer(false)} />
      ) : null}
    </section>
  );
}
